/**
 * Pinia store-readiness store (Phase 2.2 -- card
 * https://trello.com/c/hYlwyKYT).
 *
 * Backs the Store Builder readiness surfaces:
 *   * StoreReadinessCard on the Review & Launch landing page
 *   * ProductReadinessFlag in the per-product list
 *   * The Stores list active-toggle pre-flight check
 *
 * Data flow
 * ---------
 *
 * 1. ``fetchReadiness(storeId)`` -- GET /api/stores/<id>/readiness/
 *    Returns the aggregate publishable + blockers + info + placement_count
 *    payload.
 * 2. ``fetchProductsReadiness(storeId)`` -- GET /api/stores/<id>/products_readiness/
 *    Returns per-campaign-product { state, count, decoration_count } rows.
 *
 * Tenant + epoch hygiene
 * ----------------------
 *
 * Mirrors the approvalWorkspace store: an integer ``epoch`` is bumped
 * by ``clear()`` so a late-arriving fetch from a prior tenant cannot
 * repopulate state after impersonation stops.
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import api from "@/services/api";

export const useStoreReadinessStore = defineStore("storeReadiness", () => {
  // Keyed by storeId so multiple stores can be inspected on the list
  // page without thrashing a singleton.
  const readinessByStoreId = ref({});
  const productsReadinessByStoreId = ref({});
  const loadingByStoreId = ref({});
  const errorByStoreId = ref({});
  const epoch = ref(0);
  // Request token per store: the newest fetch owns the slot, so a slower,
  // older response can neither overwrite a newer answer nor flip the
  // loading flag off while the newer read is still in flight.
  const latestRequestByStoreId = {};

  function _ensureSlot(map, storeId) {
    if (!map.value[storeId]) map.value[storeId] = null;
  }

  function readinessFor(storeId) {
    return readinessByStoreId.value[storeId] || null;
  }
  function productsReadinessFor(storeId) {
    return productsReadinessByStoreId.value[storeId] || [];
  }
  function isLoading(storeId) {
    return !!loadingByStoreId.value[storeId];
  }
  function errorFor(storeId) {
    return errorByStoreId.value[storeId] || null;
  }

  /**
   * The ``launch`` block (live / can_launch / reasons) of the readiness
   * payload -- but ONLY when it is a fresh server answer: null while a read
   * is in flight and after one failed, even though the last payload is still
   * cached for the other readers (blocker card, section flags). Every
   * consumer of the launch decision (Review & Launch button and pill) reads
   * it through here, so a cached answer from before a close, a save or a
   * launch can never be shown as current.
   */
  function launchAnswerFor(storeId) {
    if (isLoading(storeId) || errorFor(storeId)) return null;
    const launch = readinessFor(storeId)?.launch;
    return launch && typeof launch === "object" ? launch : null;
  }

  function isPublishable(storeId) {
    const r = readinessFor(storeId);
    if (!r) return null; // unknown until fetched
    return r.publishable !== false;
  }

  async function fetchReadiness(storeId) {
    const startEpoch = epoch.value;
    const token = (latestRequestByStoreId[storeId] || 0) + 1;
    latestRequestByStoreId[storeId] = token;
    const isLatest = () => latestRequestByStoreId[storeId] === token;
    loadingByStoreId.value = {
      ...loadingByStoreId.value,
      [storeId]: true,
    };
    errorByStoreId.value = {
      ...errorByStoreId.value,
      [storeId]: null,
    };
    try {
      const { data } = await api.get(`/stores/${storeId}/readiness/`);
      if (startEpoch !== epoch.value) return null;
      if (!isLatest()) return data;
      readinessByStoreId.value = {
        ...readinessByStoreId.value,
        [storeId]: data,
      };
      return data;
    } catch (err) {
      if (startEpoch !== epoch.value) return null;
      if (!isLatest()) throw err;
      errorByStoreId.value = {
        ...errorByStoreId.value,
        [storeId]: err,
      };
      throw err;
    } finally {
      if (isLatest() && startEpoch === epoch.value) {
        loadingByStoreId.value = {
          ...loadingByStoreId.value,
          [storeId]: false,
        };
      }
    }
  }

  async function fetchProductsReadiness(storeId) {
    const startEpoch = epoch.value;
    try {
      const { data } = await api.get(
        `/stores/${storeId}/products_readiness/`
      );
      if (startEpoch !== epoch.value) return [];
      const rows = Array.isArray(data?.products) ? data.products : [];
      productsReadinessByStoreId.value = {
        ...productsReadinessByStoreId.value,
        [storeId]: rows,
      };
      return rows;
    } catch (err) {
      if (startEpoch !== epoch.value) return [];
      throw err;
    }
  }

  function clear() {
    readinessByStoreId.value = {};
    productsReadinessByStoreId.value = {};
    loadingByStoreId.value = {};
    errorByStoreId.value = {};
    for (const k of Object.keys(latestRequestByStoreId)) delete latestRequestByStoreId[k];
    epoch.value += 1;
  }

  return {
    // state
    readinessByStoreId,
    productsReadinessByStoreId,
    loadingByStoreId,
    errorByStoreId,
    // getters
    readinessFor,
    productsReadinessFor,
    isLoading,
    errorFor,
    isPublishable,
    launchAnswerFor,
    // actions
    fetchReadiness,
    fetchProductsReadiness,
    clear,
  };
});
